import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  isWeekend,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {
  hrIsApprovedLeaveStatus,
  hrIsBusinessActiveStatus,
  hrIsPendingWorkflowStatus,
  hrIsTerminalWorkflowStatus,
  hrNormalizeWorkflowStatus,
} from '@/modules/hr/components/hrApprovalMetricsUtils';
import {
  addCalendarDaysToKey,
  calendarDateKeyFromWallDate,
  getDisplayTimeFormat,
  getDisplayTimezone,
  todayInDisplayTimezone,
} from '@/utils/displayContext';
import { dateKeyInDisplayTimezone } from '@/utils/format';
import { formatWallClockTime } from '@/utils/displayFormatEngine';
import { formatDate, formatDisplayDateTime, formatTime } from '@/utils/format';
import { i18n } from '@/i18n';
import { getUiIntlLocale } from '@/i18n/languageContext';
import { formatIntlWeekdayShort } from '@/utils/intlCalendar';
import {
  hrCalTooltip,
  hrOvertimeAbbrev,
  hrT,
} from '@/modules/hr/components/hrI18n';

function hrCalendarIntlLocale(): string {
  return getUiIntlLocale();
}

/** Diagonal stripe background for off-days (weekends). */
export const HR_CAL_OFF_DAY_CLASS = 'hr-cal-cell-off';

/** Left border marking the start of a calendar week (Sunday). */
export const HR_CAL_WEEK_DIVIDER_CLASS =
  'border-l-[3px] border-l-slate-300 dark:border-l-slate-600';

/** Alternating background tint per calendar week (year/month timeline). */
export function hrCalendarWeekGroupClass(date: Date): string {
  return hrCalendarWeekNumber(date) % 2 === 0
    ? 'bg-sky-50/70 dark:bg-sky-950/25'
    : 'bg-violet-50/50 dark:bg-violet-950/20';
}

export function hrCalendarWeekBandClass(weekStart: Date): string {
  return hrCalendarWeekNumber(weekStart) % 2 === 0
    ? 'border-r-sky-200/90 bg-sky-100/90 text-sky-700 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-300'
    : 'border-r-violet-200/90 bg-violet-100/90 text-violet-700 dark:border-violet-800 dark:bg-violet-950/50 dark:text-violet-300';
}

/** Shared calendar grid sizing (schedule + month views). */
export const HR_CAL_DAY_COL_CLASS = 'min-w-[3.5rem] w-[3.5rem]';
export const HR_CAL_DAY_COL_WIDTH_REM = 3.5;

/** Width for week/month header bands spanning N day columns. */
export function hrCalendarBandWidth(dayCount: number): string {
  return `calc(${dayCount} * ${HR_CAL_DAY_COL_WIDTH_REM}rem)`;
}
export const HR_CAL_ROW_HEIGHT_PX = 48;
export const HR_CAL_HEADER_HEIGHT_PX = 48;
export const HR_CAL_ROW_HEIGHT_CLASS = 'h-[3rem]';
export const HR_CAL_MONTH_CELL_MIN_CLASS = 'min-h-[2.75rem]';

export function hrMonthCalendarDays(viewDate: Date): Date[] {
  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  return eachDayOfInterval({
    start: startOfWeek(monthStart),
    end: endOfWeek(monthEnd),
  }).map((day) => hrNormalizeCalendarViewDate(day));
}

/** Days within the visible month only (timeline columns for roster grids). */
export function hrMonthDaysOnly(viewDate: Date): Date[] {
  return eachDayOfInterval({
    start: startOfMonth(viewDate),
    end: endOfMonth(viewDate),
  }).map((day) => hrNormalizeCalendarViewDate(day));
}

export type HrCalendarGranularity = 'year' | 'month' | 'week' | 'day';

/** Calendar-year week number (Sunday start). Week 1 contains Jan 1; late December stays in week 52/53. */
export function hrCalendarWeekNumber(date: Date): number {
  const normalized = hrNormalizeCalendarViewDate(date);
  const year = normalized.getFullYear();
  const yearStart = hrNormalizeCalendarViewDate(new Date(year, 0, 1));
  const firstWeekStart = startOfWeek(yearStart, { weekStartsOn: 0 });
  const weekStart = startOfWeek(normalized, { weekStartsOn: 0 });
  const weekIndex = Math.floor(differenceInCalendarDays(weekStart, firstWeekStart) / 7);
  return weekIndex + 1;
}

/** First day (Sunday) of a calendar week number within a year. */
export function hrWeekStartForWeekNumber(year: number, weekNumber: number): Date {
  const yearStart = hrNormalizeCalendarViewDate(new Date(year, 0, 1));
  const firstWeekStart = startOfWeek(yearStart, { weekStartsOn: 0 });
  return hrNormalizeCalendarViewDate(addWeeks(firstWeekStart, Math.max(0, weekNumber - 1)));
}

export function hrWeekRangeForWeekNumber(year: number, weekNumber: number): { start: string; end: string } {
  const weekStart = hrWeekStartForWeekNumber(year, weekNumber);
  return hrCalendarRangeForGranularity(weekStart, 'week');
}

export function hrMonthRangeForYearMonth(year: number, month: number): { start: string; end: string } {
  const monthStart = hrNormalizeCalendarViewDate(new Date(year, month - 1, 1));
  return hrCalendarRangeForGranularity(monthStart, 'month');
}

export function hrIsCalendarWeekStart(date: Date): boolean {
  return isSameDay(date, startOfWeek(date, { weekStartsOn: 0 }));
}

/** Split visible days into calendar week groups (partial weeks at range edges are kept). */
export function hrChunkCalendarWeeks(days: Date[]): Date[][] {
  if (days.length === 0) return [];
  const weeks: Date[][] = [];
  let current: Date[] = [];
  for (const day of days) {
    if (current.length > 0 && hrIsCalendarWeekStart(day)) {
      weeks.push(current);
      current = [];
    }
    current.push(day);
  }
  if (current.length > 0) weeks.push(current);
  return weeks;
}

/** Split visible days into calendar month groups (year timeline). */
export function hrChunkCalendarMonths(days: Date[]): Date[][] {
  if (days.length === 0) return [];
  const months: Date[][] = [];
  let current: Date[] = [];
  for (const day of days) {
    if (current.length > 0 && day.getDate() === 1) {
      months.push(current);
      current = [];
    }
    current.push(day);
  }
  if (current.length > 0) months.push(current);
  return months;
}

/** Month label for year-view month bands (e.g. January). */
export function hrCalendarMonthBandLabel(date: Date): string {
  return new Intl.DateTimeFormat(hrCalendarIntlLocale(), {
    month: 'long',
    timeZone: getDisplayTimezone(),
  }).format(date);
}

/** Stable midday anchor avoids timezone edge cases when shifting calendar views. */
export function hrNormalizeCalendarViewDate(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(12, 0, 0, 0);
  return normalized;
}

export function hrWeekDaysOnly(viewDate: Date): Date[] {
  const start = startOfWeek(viewDate, { weekStartsOn: 0 });
  const end = endOfWeek(viewDate, { weekStartsOn: 0 });
  return eachDayOfInterval({ start, end }).map((day) => hrNormalizeCalendarViewDate(day));
}

export function hrYearDaysOnly(viewDate: Date): Date[] {
  const year = viewDate.getFullYear();
  return eachDayOfInterval({
    start: hrNormalizeCalendarViewDate(new Date(year, 0, 1)),
    end: hrNormalizeCalendarViewDate(new Date(year, 11, 31)),
  });
}

export function hrCalendarDaysForGranularity(viewDate: Date, granularity: HrCalendarGranularity): Date[] {
  if (granularity === 'day') return [hrNormalizeCalendarViewDate(viewDate)];
  if (granularity === 'week') return hrWeekDaysOnly(viewDate);
  if (granularity === 'year') return hrYearDaysOnly(viewDate);
  return hrMonthDaysOnly(viewDate);
}

export function hrCalendarRangeForGranularity(viewDate: Date, granularity: HrCalendarGranularity): { start: string; end: string } {
  const days = hrCalendarDaysForGranularity(viewDate, granularity);
  const start = hrDateKey(days[0]);
  const end = hrDateKey(days[days.length - 1]);
  return { start, end };
}

export function hrShiftCalendarDate(
  viewDate: Date,
  granularity: HrCalendarGranularity,
  delta: number,
): Date {
  const base = hrNormalizeCalendarViewDate(viewDate);
  if (granularity === 'day') return addDays(base, delta);
  if (granularity === 'week') return addWeeks(base, delta);
  if (granularity === 'year') return addYears(base, delta);
  return addMonths(base, delta);
}

/** Snap the view anchor when switching calendar granularity. */
export function hrCalendarAnchorDate(granularity: HrCalendarGranularity, date = new Date()): Date {
  const normalized = hrNormalizeCalendarViewDate(date);
  if (granularity === 'week') {
    return hrNormalizeCalendarViewDate(startOfWeek(normalized, { weekStartsOn: 0 }));
  }
  return normalized;
}

/** Default calendar entry: current year view anchored on today. */
export function hrDefaultCalendarViewDate(): Date {
  return hrNormalizeCalendarViewDate(new Date());
}

/** Horizontal scroll target — week containing `date` (year), or best in-range day (month). */
export function hrCalendarScrollAnchorKey(
  granularity: HrCalendarGranularity,
  date = new Date(),
  visibleDays?: Date[],
): string {
  const normalized = hrNormalizeCalendarViewDate(date);
  if (granularity === 'year') {
    return hrDateKey(startOfWeek(normalized, { weekStartsOn: 0 }));
  }
  if (granularity === 'month') {
    const weekStartKey = hrDateKey(startOfWeek(normalized, { weekStartsOn: 0 }));
    const visibleKeys = new Set((visibleDays ?? []).map((day) => hrDateKey(day)));
    if (visibleKeys.has(weekStartKey)) return weekStartKey;
    const todayKey = hrDateKey(normalized);
    if (visibleKeys.has(todayKey)) return todayKey;
    const firstInWeek = (visibleDays ?? [])
      .map((day) => hrDateKey(day))
      .filter((dayKey) => dayKey >= weekStartKey)
      .sort()[0];
    if (firstInWeek) return firstInWeek;
    return weekStartKey;
  }
  return hrDateKey(normalized);
}

/** Toolbar title for year / month / week / day calendar modes. */
export function hrFormatTimelineNavTitle(viewDate: Date, granularity: HrCalendarGranularity): string {
  const locale = hrCalendarIntlLocale();
  const timeZone = getDisplayTimezone();
  const formatPart = (date: Date, options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(date);

  if (granularity === 'year') {
    return formatPart(viewDate, { year: 'numeric' });
  }

  if (granularity === 'day') {
    const weekLabel = hrCalendarWeekShortPrefix(viewDate);
    return `${weekLabel}${formatPart(viewDate, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }

  if (granularity === 'week') {
    const weekDays = hrWeekDaysOnly(viewDate);
    const start = weekDays[0];
    const end = weekDays[weekDays.length - 1];
    const weekLabel = hrCalendarWeekShortPrefix(viewDate);
    if (isSameMonth(start, end)) {
      return `${weekLabel}${formatPart(start, { month: 'long', day: 'numeric' })} – ${formatPart(end, {
        day: 'numeric',
        year: 'numeric',
      })}`;
    }
    return `${weekLabel}${formatPart(start, { month: 'short', day: 'numeric' })} – ${formatPart(end, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }

  return hrFormatTimelineMonthTitle(viewDate);
}

export function hrWeekdayLabel(date: Date): string {
  return formatIntlWeekdayShort(hrNormalizeCalendarViewDate(date), hrCalendarIntlLocale());
}

/** Uppercase weekday abbreviation for calendar column headers (e.g. MON, LUN.). */
export function hrWeekdayAbbrevLabel(date: Date): string {
  return formatIntlWeekdayShort(hrNormalizeCalendarViewDate(date), hrCalendarIntlLocale(), {
    uppercase: true,
  });
}

export function hrWeekdayFullLabel(date: Date): string {
  return new Intl.DateTimeFormat(hrCalendarIntlLocale(), {
    weekday: 'long',
    timeZone: getDisplayTimezone(),
  }).format(hrNormalizeCalendarViewDate(date));
}

/** Localized week band label, e.g. "Week 12" / "Semaine 12". */
export function hrFormatWeekNumberLabel(weekStart: Date): string {
  const n = hrCalendarWeekNumber(weekStart);
  if (i18n.isInitialized) {
    return i18n.t('calendar.weekNumber', { ns: 'hr', n });
  }
  return `Week ${n}`;
}

function hrCalendarWeekShortPrefix(date: Date): string {
  const n = hrCalendarWeekNumber(date);
  if (i18n.isInitialized) {
    return i18n.t('calendar.weekShortPrefix', { ns: 'hr', n });
  }
  return `W${n} · `;
}

/** Month title for employee timeline grids (company locale + timezone). */
export function hrFormatTimelineMonthTitle(viewDate: Date): string {
  return new Intl.DateTimeFormat(hrCalendarIntlLocale(), {
    month: 'long',
    year: 'numeric',
    timeZone: getDisplayTimezone(),
  }).format(viewDate);
}

/** Column header for timeline grids: weekday plus company date format. */
export function hrFormatTimelineColumnHeader(date: Date): string {
  return `${hrWeekdayFullLabel(date)} ${formatDate(hrDateKey(date))}`;
}

/** Day-of-month label for timeline cell corners. */
export function hrFormatTimelineDayNumber(date: Date): string {
  return new Intl.DateTimeFormat(hrCalendarIntlLocale(), {
    day: 'numeric',
    timeZone: getDisplayTimezone(),
  }).format(hrNormalizeCalendarViewDate(date));
}

export function hrIsWeekendDay(date: Date): boolean {
  return isWeekend(date);
}

/** Default working hours per leave day when API returns days only. */
export const HR_DEFAULT_HOURS_PER_LEAVE_DAY = 8;

function formatHoursValue(totalHours: number): string {
  const rounded = Math.round(totalHours * 10) / 10;
  return Number.isInteger(rounded) ? String(Math.round(rounded)) : rounded.toFixed(1);
}

/** Display duration in hours, e.g. "8 hrs", "7.5 hrs". */
export function hrFormatHours(totalHours: number): string {
  if (!Number.isFinite(totalHours)) return '—';
  const value = formatHoursValue(totalHours);
  if (i18n.isInitialized) {
    return i18n.t('calendar.units.hoursCount', { ns: 'hr', value });
  }
  return `${value} hrs`;
}

/** Convert minutes to hours for display. */
export function hrFormatMinutes(minutes?: number | null): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—';
  return hrFormatHours(minutes / 60);
}

/** Display a day count for leave, e.g. "4 days", "0.5 days", "1 day". */
export function hrFormatDaysCount(days?: number | null): string {
  if (days == null || !Number.isFinite(days)) return '—';
  const n = Number(days);
  const rounded = Math.round(n * 10) / 10;
  const value = Number.isInteger(rounded) ? String(Math.round(rounded)) : rounded.toFixed(1);
  if (i18n.isInitialized) {
    const key = rounded === 1 ? 'calendar.units.dayCountOne' : 'calendar.units.dayCountOther';
    return i18n.t(key, { ns: 'hr', value });
  }
  return `${value} ${rounded === 1 ? 'day' : 'days'}`;
}

/** Format leave duration in days (alias for leave lists and previews). */
export function hrFormatLeaveDays(days?: number | null): string {
  return hrFormatDaysCount(days);
}

export function hrShiftDurationHours(start?: string, end?: string): number {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return 0;
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return mins / 60;
}

/** Stable yyyy-MM-dd key for calendar grid days and API date strings. */
export function hrDateKey(date: Date | string): string {
  if (typeof date === 'string') {
    return date.length >= 10 ? date.slice(0, 10) : date;
  }
  return calendarDateKeyFromWallDate(date);
}

export function hrMonthRange(viewDate: Date): { start: string; end: string } {
  const days = hrMonthDaysOnly(viewDate);
  return {
    start: hrDateKey(days[0]),
    end: hrDateKey(days[days.length - 1]),
  };
}

export function hrDateInRange(dateKey: string, start: string, end: string): boolean {
  return dateKey >= start && dateKey <= end;
}

export function hrLeaveSpansDate(startDate: string, endDate: string, dayKey: string): boolean {
  return hrDateInRange(dayKey, startDate, endDate);
}

function nextDateKey(dateKey: string): string {
  return addCalendarDaysToKey(dateKey, 1);
}

/** Day keys within range where a leave request overlaps. */
export function hrExpandLeaveDays(
  startDate: string,
  endDate: string,
  rangeStart: string,
  rangeEnd: string,
): string[] {
  const start = startDate.slice(0, 10);
  const end = endDate.slice(0, 10);
  const from = start > rangeStart ? start : rangeStart;
  const to = end < rangeEnd ? end : rangeEnd;
  const days: string[] = [];
  let cursor = from;
  while (cursor <= to) {
    if (hrLeaveSpansDate(start, end, cursor)) {
      days.push(cursor);
    }
    const next = nextDateKey(cursor);
    if (next <= cursor) break;
    cursor = next;
  }
  return days;
}

export type DateRangeRecord = {
  selectedDates?: string[] | null;
  countedLeaveDates?: string[] | null;
  startDate: string;
  endDate: string;
};

type ExpandRecordDaysOptions = {
  /** When counted leave dates are absent, drop non-working days (holidays/weekends). */
  isScheduledWorkDay?: (dayKey: string) => boolean;
};

function hrRecordDaySource(record: DateRangeRecord): string[] | null {
  if (record.countedLeaveDates && record.countedLeaveDates.length > 0) {
    return record.countedLeaveDates;
  }
  return record.selectedDates ?? null;
}

/** Expands explicit selected/counted days when present; otherwise uses start–end range. */
export function hrExpandRecordDays(
  record: DateRangeRecord,
  rangeStart: string,
  rangeEnd: string,
  options?: ExpandRecordDaysOptions,
): string[] {
  const source = hrRecordDaySource(record);
  if (source && source.length > 0) {
    let days = [...new Set(source.map((d) => d.slice(0, 10)))]
      .filter((d) => d >= rangeStart && d <= rangeEnd)
      .sort();
    if (
      !(record.countedLeaveDates && record.countedLeaveDates.length > 0) &&
      options?.isScheduledWorkDay
    ) {
      days = days.filter((dayKey) => options.isScheduledWorkDay!(dayKey));
    }
    return days;
  }
  return hrExpandLeaveDays(record.startDate, record.endDate, rangeStart, rangeEnd);
}

function hrAreDatesContiguous(sorted: string[]): boolean {
  if (sorted.length <= 1) return true;
  for (let i = 1; i < sorted.length; i += 1) {
    if (nextDateKey(sorted[i - 1]) !== sorted[i]) return false;
  }
  return true;
}

export function hrFormatRecordDaysLabel(record: DateRangeRecord): string {
  const source = hrRecordDaySource(record);
  if (source && source.length > 0) {
    const sorted = [...new Set(source.map((d) => d.slice(0, 10)))].sort();
    if (sorted.length === 1) return formatDate(sorted[0]);
    if (hrAreDatesContiguous(sorted)) {
      if (sorted.length === 2) {
        return `${formatDate(sorted[0])} & ${formatDate(sorted[1])}`;
      }
      return `${formatDate(sorted[0])} – ${formatDate(sorted[sorted.length - 1])} (${sorted.length} days)`;
    }
    const preview = sorted
      .slice(0, 4)
      .map((d) => formatDate(d))
      .join(', ');
    const suffix = sorted.length > 4 ? ` +${sorted.length - 4} more` : '';
    return `${sorted.length} days: ${preview}${suffix}`;
  }
  if (record.startDate === record.endDate) return formatDate(record.startDate);
  return `${formatDate(record.startDate)} → ${formatDate(record.endDate)}`;
}

/** Preview line for leave creation — working days plus the dates that actually count. */
export function hrFormatLeavePreviewSummary(preview: {
  workingDays: number;
  countedLeaveDates?: string[] | null;
}): string {
  const daysLabel = hrFormatDaysCount(preview.workingDays);
  const counted = (preview.countedLeaveDates ?? [])
    .map((d) => d.slice(0, 10))
    .filter(Boolean)
    .sort();
  if (counted.length === 0) return daysLabel;
  const datesLabel = hrFormatRecordDaysLabel({
    countedLeaveDates: counted,
    startDate: counted[0],
    endDate: counted[counted.length - 1],
  });
  return `${daysLabel} · ${datesLabel}`;
}

export function hrAttendanceDayKey(checkIn?: string | null): string | null {
  if (!checkIn) return null;
  try {
    return dateKeyInDisplayTimezone(checkIn);
  } catch {
    return checkIn.length >= 10 ? checkIn.slice(0, 10) : null;
  }
}

type AttendanceDayRow = {
  checkIn?: string;
  checkOut?: string;
  attendanceStatus: string;
  workedMinutes?: number | null;
  productiveMinutes?: number | null;
  lateMinutes?: number | null;
  overtimeMinutes?: number | null;
  checkInMode?: string;
  approvalStatus?: string;
};

/** Minutes credited for one attendance row (productive time preferred). */
export function hrAttendanceRowMinutes(row: AttendanceDayRow): number {
  const minutes = row.productiveMinutes ?? row.workedMinutes;
  return minutes != null && Number.isFinite(minutes) ? minutes : 0;
}

export function hrSumAttendanceMinutes(rows: AttendanceDayRow[]): number {
  return rows.reduce((sum, row) => sum + hrAttendanceRowMinutes(row), 0);
}

const ATTENDANCE_STATUS_RANK: Record<string, number> = {
  ABSENT: 5,
  LATE: 4,
  EARLY: 3,
  HALF_DAY: 2,
  PRESENT: 1,
};

/** Pick the most severe status when multiple punches exist on one day. */
export function hrPrimaryAttendanceStatus(rows: AttendanceDayRow[]): string {
  if (rows.length === 0) return 'PRESENT';
  return rows.reduce((worst, row) => {
    const rank = ATTENDANCE_STATUS_RANK[row.attendanceStatus] ?? 0;
    const worstRank = ATTENDANCE_STATUS_RANK[worst] ?? 0;
    return rank > worstRank ? row.attendanceStatus : worst;
  }, rows[0].attendanceStatus);
}

export type HrShiftAttendanceRow = AttendanceDayRow & { shiftId?: number };

/** Aggregate per-shift attendance into a daily summary label. */
export function hrDailyAttendanceSummary(
  shifts: Array<{ shiftId: number; shiftName?: string }>,
  rows: HrShiftAttendanceRow[],
  leaveShiftIds: Set<number> = new Set(),
): { status: string; presentShifts: number; totalShifts: number } {
  const totalShifts = shifts.length;
  if (totalShifts === 0) {
    return { status: 'NOT_SCHEDULED', presentShifts: 0, totalShifts: 0 };
  }
  let presentShifts = 0;
  let leaveShifts = 0;
  for (const shift of shifts) {
    if (leaveShiftIds.has(shift.shiftId)) {
      leaveShifts++;
      continue;
    }
    const hasPunch = rows.some(
      (r) => r.shiftId === shift.shiftId || (r.shiftId == null && totalShifts === 1),
    );
    if (hasPunch) presentShifts++;
  }
  const absentShifts = totalShifts - presentShifts - leaveShifts;
  let status = 'PARTIAL';
  if (presentShifts === totalShifts) status = 'PRESENT';
  else if (leaveShifts === totalShifts) status = 'LEAVE';
  else if (absentShifts === totalShifts) status = 'ABSENT';
  else if (presentShifts > 0 && leaveShifts > 0) status = 'HALF_LEAVE_PRESENT';
  return { status, presentShifts, totalShifts };
}

/** Check-in/out clock time in the active company timezone and time format policy. */
export function hrFormatAttendanceClock(iso?: string | null): string {
  if (!iso) return '—';
  return formatTime(iso);
}

/** Full check-in/out timestamp for lists and detail views. */
export function hrFormatAttendanceDateTime(iso?: string | null): string {
  if (!iso) return '—';
  return formatDisplayDateTime(iso);
}

/** Compact in → out label; same-day punches use company time format only. */
export function hrFormatAttendanceInOut(checkIn?: string | null, checkOut?: string | null): string {
  if (!checkIn && !checkOut) return '—';
  if (!checkOut) return hrFormatAttendanceDateTime(checkIn);
  const inDay = hrAttendanceDayKey(checkIn);
  const outDay = hrAttendanceDayKey(checkOut);
  if (inDay && outDay && inDay === outDay) {
    return `${hrFormatAttendanceClock(checkIn)} → ${hrFormatAttendanceClock(checkOut)}`;
  }
  return `${hrFormatAttendanceDateTime(checkIn)} → ${hrFormatAttendanceDateTime(checkOut)}`;
}

/** Format a shift clock value using the company time format policy. */
export function hrFormatShiftClockTime(value?: string | null): string {
  if (!value) return '';
  const timePart = value.includes('T') ? value.slice(11, 19) : value;
  const match = /^(\d{1,2}):(\d{2})/.exec(timePart);
  if (!match) return value;
  return formatWallClockTime(`${match[1].padStart(2, '0')}:${match[2]}:00`, getDisplayTimeFormat());
}

export function hrFormatShiftTimeRange(start?: string | null, end?: string | null): string {
  if (!start && !end) return '';
  const from = hrFormatShiftClockTime(start);
  const to = hrFormatShiftClockTime(end);
  if (from && to) return `${from} – ${to}`;
  return from || to;
}

export const HR_DEFAULT_SHIFT_COLOR = '#64748b';

export type HrShiftScheduleInfo = {
  shiftId?: number | null;
  shiftName?: string | null;
  shiftStartTime?: string | null;
  shiftEndTime?: string | null;
  shiftColor?: string | null;
  expectedMinutes?: number;
};

export const HR_DEFAULT_SHIFT_LABEL = 'Default';

export type HrShiftCornerSegment = {
  label: string;
  style?: Record<string, string>;
};

export function hrIsCompanyDefaultShift(
  info?: Pick<HrShiftScheduleInfo, 'shiftId' | 'shiftName' | 'shiftStartTime' | 'shiftEndTime'> | HrResolvedShiftSlotLike | null,
): boolean {
  if (!info) return false;
  const name = info.shiftName?.trim();
  if (!name) {
    return Boolean(info.shiftStartTime || info.shiftEndTime);
  }
  if (!info.shiftId && name === HR_DEFAULT_SHIFT_LABEL) {
    return true;
  }
  return false;
}

export function hrResolveShiftDisplayColor(
  info?: HrShiftScheduleInfo | null,
): string | undefined {
  if (!info || !hrHasShiftScheduleDisplay(info)) return undefined;
  if (hrIsCompanyDefaultShift(info)) return HR_DEFAULT_SHIFT_COLOR;
  const color = info.shiftColor?.trim();
  if (color) return hrNormalizeLeaveTypeColor(color);
  return HR_DEFAULT_SHIFT_COLOR;
}

export function hrResolveShiftCornerLabel(
  info?: HrShiftScheduleInfo & { scheduledWorkDay?: boolean } | null,
): string | undefined {
  const name = info?.shiftName?.trim();
  if (name) return name;
  if (info?.shiftStartTime || info?.shiftEndTime) return HR_DEFAULT_SHIFT_LABEL;
  return undefined;
}

export function hrPendingShiftCornerLabel(info?: HrShiftScheduleInfo | null): string | undefined {
  const base = hrResolveShiftCornerLabel(info);
  if (!base) return 'Shift · pending';
  return `${base} · pending`;
}

export function hrResolveShiftCornerStyle(
  info?: HrShiftScheduleInfo | null,
): Record<string, string> | undefined {
  const color = hrResolveShiftDisplayColor(info);
  if (!color || !hrResolveShiftCornerLabel(info)) return undefined;
  return hrShiftNameStyle(color);
}

export function hrHasShiftScheduleDisplay(
  info?: HrShiftScheduleInfo | null,
): boolean {
  if (!info) return false;
  return Boolean(info.shiftName?.trim() || info.shiftStartTime || info.shiftEndTime);
}

/** Pending shift change wins over an approved assignment for calendar display. */
export function hrEffectiveShiftDisplay<
  T extends HrShiftScheduleInfo,
  P extends HrShiftScheduleInfo & { statusCode?: string },
>(resolved?: T | null, pending?: P | null): (T | P) & { pending?: boolean } | null {
  if (pending) {
    return { ...pending, pending: true };
  }
  if (resolved && hrHasShiftScheduleDisplay(resolved)) {
    return { ...resolved, pending: false };
  }
  return null;
}

export function hrFormatShiftTimeLines(
  start?: string | null,
  end?: string | null,
): { startLine?: string; endLine?: string } {
  const from = hrFormatShiftClockTime(start);
  const to = hrFormatShiftClockTime(end);
  if (from && to) return { startLine: from, endLine: to };
  if (from) return { startLine: from };
  if (to) return { endLine: to };
  return {};
}

/** Tooltip: shift time + expected hours. */
export function hrFormatShiftScheduleTooltip(info: HrShiftScheduleInfo): string {
  const parts: string[] = [];
  const times = hrFormatShiftTimeRange(info.shiftStartTime, info.shiftEndTime);
  if (times) parts.push(times);
  if (info.expectedMinutes != null && info.expectedMinutes > 0) {
    parts.push(`${hrFormatMinutes(info.expectedMinutes)} expected`);
  }
  if (!times && info.shiftName?.trim()) parts.push(info.shiftName.trim());
  return parts.join(' · ');
}

export type HrResolvedShiftSlotLike = HrShiftScheduleInfo & {
  shiftId?: number;
  shiftName?: string | null;
  sequenceOrder?: number;
};

/** Normalize API day payload to an ordered list of shift slots for display. */
export function hrResolvedDayShifts(
  resolved?: (HrShiftScheduleInfo & {
    shiftId?: number;
    shiftName?: string | null;
    shifts?: HrResolvedShiftSlotLike[];
  }) | null,
): HrResolvedShiftSlotLike[] {
  if (!resolved) return [];
  if (resolved.shifts && resolved.shifts.length > 0) {
    return [...resolved.shifts].sort(
      (a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0),
    );
  }
  if (hrHasShiftScheduleDisplay(resolved)) {
    return [
      {
        shiftId: resolved.shiftId,
        shiftName: resolved.shiftName,
        shiftStartTime: resolved.shiftStartTime,
        shiftEndTime: resolved.shiftEndTime,
        shiftColor: resolved.shiftColor,
        expectedMinutes: resolved.expectedMinutes,
        sequenceOrder: 1,
      },
    ];
  }
  return [];
}

/** Tooltip for one or more scheduled shifts on a day. */
export function hrFormatMultiShiftScheduleTooltip(
  shifts: HrResolvedShiftSlotLike[],
): string {
  if (shifts.length === 0) return '';
  return shifts
    .map((shift) => {
      const name = shift.shiftName?.trim();
      const times = hrFormatShiftTimeRange(shift.shiftStartTime, shift.shiftEndTime);
      const expected =
        shift.expectedMinutes != null && shift.expectedMinutes > 0
          ? `${hrFormatMinutes(shift.expectedMinutes)} expected`
          : '';
      const parts: string[] = [];
      if (name) parts.push(name);
      if (times) parts.push(times);
      if (expected) parts.push(expected);
      return parts.join(' ');
    })
    .join(' · ');
}

/** Corner badge label for cells with multiple shifts. */
export function hrResolveMultiShiftCornerLabel(
  shifts: HrResolvedShiftSlotLike[],
): string | undefined {
  const segments = hrResolveMultiShiftCornerSegments(shifts);
  if (!segments || segments.length === 0) return undefined;
  if (segments.length === 1) return segments[0].label;
  if (segments.length === 2) return `${segments[0].label} + ${segments[1].label}`;
  return segments[0].label;
}

function hrResolveShiftSlotCornerLabel(shift: HrResolvedShiftSlotLike): string | null {
  const name = shift.shiftName?.trim();
  if (name) return name;
  if (shift.shiftStartTime || shift.shiftEndTime) return HR_DEFAULT_SHIFT_LABEL;
  return null;
}

/** Per-shift corner segments so each name can use its own color. */
export function hrResolveMultiShiftCornerSegments(
  shifts: HrResolvedShiftSlotLike[],
): HrShiftCornerSegment[] | undefined {
  if (shifts.length === 0) return undefined;
  if (shifts.length === 1) {
    const label = hrResolveShiftCornerLabel(shifts[0]);
    if (!label) return undefined;
    return [{ label, style: hrResolveShiftCornerStyle(shifts[0]) }];
  }
  const segments: HrShiftCornerSegment[] = [];
  for (const shift of shifts) {
    const label = hrResolveShiftSlotCornerLabel(shift);
    if (label) {
      segments.push({ label, style: hrResolveShiftCornerStyle(shift) });
    }
  }
  if (segments.length === 0) return undefined;
  if (segments.length <= 2) return segments;
  return [{ label: `${shifts.length} shifts`, style: hrResolveMultiShiftCornerStyle(shifts) }];
}

export function hrPendingShiftCornerSegments(
  info?: HrShiftScheduleInfo | null,
): HrShiftCornerSegment[] | undefined {
  const label = hrPendingShiftCornerLabel(info);
  if (!label) return undefined;
  return [{ label, style: hrResolveShiftCornerStyle(info) }];
}

/** @deprecated Prefer hrResolveMultiShiftCornerSegments for multi-shift cells. */
export function hrResolveMultiShiftCornerStyle(
  shifts: HrResolvedShiftSlotLike[],
): Record<string, string> | undefined {
  if (shifts.length === 0) return undefined;
  if (shifts.length === 1) return hrResolveShiftCornerStyle(shifts[0]);
  const namedWithColor = shifts.find(
    (shift) => !hrIsCompanyDefaultShift(shift) && shift.shiftColor?.trim(),
  );
  if (namedWithColor) return hrResolveShiftCornerStyle(namedWithColor);
  return hrResolveShiftCornerStyle(shifts[0]);
}

export function hrPendingShiftAddsToDay(
  pendingShiftId: number | undefined,
  resolved?: { shifts?: HrResolvedShiftSlotLike[]; shiftId?: number } | null,
): boolean {
  if (pendingShiftId == null) return false;
  const existing = hrResolvedDayShifts(resolved ?? null);
  if (existing.length === 0) return false;
  return !existing.some((shift) => shift.shiftId === pendingShiftId);
}

export function hrHasScheduledShift(info?: HrShiftScheduleInfo | null): boolean {
  if (!info) return false;
  return Boolean(
    (info.expectedMinutes != null && info.expectedMinutes > 0) ||
      info.shiftStartTime ||
      info.shiftEndTime ||
      info.shiftName,
  );
}

function hrAttendanceRowApproved(row: AttendanceDayRow): boolean {
  return hrIsBusinessActiveStatus(row.approvalStatus);
}

/** Hover tooltip: approved, pending, and terminal worked time with punch detail. */
export function hrAttendanceDayTooltip(rows: AttendanceDayRow[]): string {
  if (rows.length === 0) return '';
  const approved = rows.filter((row) => hrAttendanceRowApproved(row));
  const pending = rows.filter((row) => hrIsPendingWorkflowStatus(row.approvalStatus));
  const inactive = rows.filter((row) => hrIsTerminalWorkflowStatus(row.approvalStatus));
  const lines: string[] = [];

  const appendSection = (label: string, sectionRows: AttendanceDayRow[]) => {
    if (sectionRows.length === 0) return;
    const total = hrSumAttendanceMinutes(sectionRows);
    lines.push(
      `${label}: ${hrFormatMinutes(total)}${
        sectionRows.length > 1
          ? ` ${hrT('calendar.tooltip.punches', `(${sectionRows.length} punches)`, { count: sectionRows.length })}`
          : ''
      }`,
    );
    const sorted = [...sectionRows].sort((a, b) => (a.checkIn ?? '').localeCompare(b.checkIn ?? ''));
    for (const row of sorted) {
      const inOut = `${hrFormatAttendanceClock(row.checkIn)} – ${hrFormatAttendanceClock(row.checkOut)}`;
      const mins = hrAttendanceRowMinutes(row);
      const otAbbrev = hrOvertimeAbbrev();
      const extras = [
        row.attendanceStatus,
        mins > 0 ? hrFormatMinutes(mins) : undefined,
        row.lateMinutes
          ? hrT('calendar.tooltip.late', `late ${hrFormatMinutes(row.lateMinutes)}`, {
              minutes: hrFormatMinutes(row.lateMinutes),
            })
          : undefined,
        row.overtimeMinutes
          ? hrT('calendar.overtimeMinutesLabel', `${hrFormatMinutes(row.overtimeMinutes)} ${otAbbrev}`, {
              minutes: hrFormatMinutes(row.overtimeMinutes),
              abbrev: otAbbrev,
            })
          : undefined,
        row.checkInMode,
        !hrAttendanceRowApproved(row)
          ? hrNormalizeWorkflowStatus(row.approvalStatus) || 'PENDING'
          : undefined,
      ].filter(Boolean);
      lines.push(`  • ${inOut} · ${extras.join(' · ')}`);
    }
  };

  appendSection(hrCalTooltip('workedApproved', 'Worked (approved)'), approved);
  appendSection(hrCalTooltip('workedPending', 'Worked (pending)'), pending);
  if (inactive.length > 0) {
    const rejected = inactive.filter((row) => hrNormalizeWorkflowStatus(row.approvalStatus) === 'REJECTED');
    const cancelled = inactive.filter((row) => hrNormalizeWorkflowStatus(row.approvalStatus) === 'CANCELLED');
    appendSection(hrCalTooltip('workedRejected', 'Worked (rejected)'), rejected);
    appendSection(hrCalTooltip('workedCancelled', 'Worked (cancelled)'), cancelled);
  }
  return lines.join('\n');
}

export type HrLeaveDayTooltipRow = {
  leaveTypeId: number;
  statusCode: string;
  durationMode?: string | null;
  totalDays: number;
};

/** Hover tooltip: approved, pending, and terminal leave with type and duration detail. */
export function hrLeaveDayTooltip(
  rows: HrLeaveDayTooltipRow[],
  resolveTypeName: (leaveTypeId: number) => string | undefined,
): string {
  if (rows.length === 0) return '';
  const approved = rows.filter((row) => hrIsApprovedLeaveStatus(row.statusCode));
  const pending = rows.filter((row) => hrIsPendingWorkflowStatus(row.statusCode));
  const inactive = rows.filter((row) => hrIsTerminalWorkflowStatus(row.statusCode));
  const lines: string[] = [];

  const appendSection = (label: string, sectionRows: HrLeaveDayTooltipRow[]) => {
    if (sectionRows.length === 0) return;
    const dayTotal = sectionRows.reduce((sum, row) => sum + Number(row.totalDays ?? 0), 0);
    lines.push(`${label}: ${hrFormatLeaveDays(dayTotal)}`);
    for (const row of sectionRows) {
      const typeName = resolveTypeName(row.leaveTypeId) ?? hrT('calendar.tooltip.leaveTypeFallback', 'Leave');
      const duration =
        row.durationMode && row.durationMode !== 'FULL_DAY'
          ? row.durationMode.replace(/_/g, ' ').toLowerCase()
          : hrT('calendar.tooltip.fullDay', 'full day');
      lines.push(
        `  • ${typeName} · ${hrNormalizeWorkflowStatus(row.statusCode) || 'PENDING'} · ${duration} · ${hrFormatLeaveDays(row.totalDays)}`,
      );
    }
  };

  appendSection(hrCalTooltip('leaveApproved', 'Leave (approved)'), approved);
  appendSection(hrCalTooltip('leavePending', 'Leave (pending)'), pending);
  if (inactive.length > 0) {
    const rejected = inactive.filter((row) => hrNormalizeWorkflowStatus(row.statusCode) === 'REJECTED');
    const cancelled = inactive.filter((row) => hrNormalizeWorkflowStatus(row.statusCode) === 'CANCELLED');
    appendSection(hrCalTooltip('leaveRejected', 'Leave (rejected)'), rejected);
    appendSection(hrCalTooltip('leaveCancelled', 'Leave (cancelled)'), cancelled);
  }
  return lines.join('\n');
}

export function hrIsApprovedOvertimeStatus(statusCode?: string | null): boolean {
  return (statusCode ?? '').trim().toUpperCase() === 'APPROVED';
}

export function hrSumOvertimeMinutes(requests: { minutes: number }[]): number {
  return requests.reduce((sum, row) => sum + (row.minutes ?? 0), 0);
}

export type HrOvertimeDayTooltipRow = {
  minutes: number;
  overtimeType: string;
  statusCode: string;
  attendanceId?: number | null;
};

export function hrOvertimeDayTooltip(rows: HrOvertimeDayTooltipRow[]): string {
  if (rows.length === 0) return '';
  const approved = rows.filter((row) => hrIsApprovedOvertimeStatus(row.statusCode));
  const pending = rows.filter((row) => hrIsPendingWorkflowStatus(row.statusCode));
  const inactive = rows.filter((row) => hrIsTerminalWorkflowStatus(row.statusCode));
  const lines: string[] = [];

  const appendSection = (label: string, sectionRows: HrOvertimeDayTooltipRow[]) => {
    if (sectionRows.length === 0) return;
    lines.push(`${label}: ${hrFormatMinutes(hrSumOvertimeMinutes(sectionRows))}`);
    for (const row of sectionRows) {
      const source = row.attendanceId != null
        ? hrCalTooltip('fromAttendance', 'from attendance')
        : hrCalTooltip('manualRequest', 'manual request');
      const status = hrNormalizeWorkflowStatus(row.statusCode);
      lines.push(
        `  • ${hrFormatMinutes(row.minutes)} · ${row.overtimeType} · ${source}${status && !hrIsApprovedOvertimeStatus(row.statusCode) ? ` · ${status}` : ''}`,
      );
    }
  };

  appendSection(hrCalTooltip('overtimeApproved', 'OT (approved)'), approved);
  appendSection(hrCalTooltip('overtimePending', 'OT (pending)'), pending);
  if (inactive.length > 0) {
    const rejected = inactive.filter((row) => hrNormalizeWorkflowStatus(row.statusCode) === 'REJECTED');
    const cancelled = inactive.filter((row) => hrNormalizeWorkflowStatus(row.statusCode) === 'CANCELLED');
    appendSection(hrCalTooltip('overtimeRejected', 'OT (rejected)'), rejected);
    appendSection(hrCalTooltip('overtimeCancelled', 'OT (cancelled)'), cancelled);
  }
  return lines.join('\n');
}

export function hrOvertimeCalendarLabel(totalMinutes: number): string {
  const minutes = hrFormatMinutes(totalMinutes);
  const abbrev = hrOvertimeAbbrev();
  return hrT('calendar.overtimeMinutesLabel', `${minutes} ${abbrev}`, { minutes, abbrev });
}

export type HrDayOvertimeSummary = {
  approvedMinutes: number;
  pendingMinutes: number;
};

/** Combines overtime requests with attendance OT not yet linked to a request. */
export function hrDayOvertimeSummary(
  overtimeRequests: HrOvertimeDayTooltipRow[],
  attendanceRows: Array<AttendanceDayRow & { id?: number }>,
  linkedAttendanceIds: ReadonlySet<number>,
): HrDayOvertimeSummary {
  let approvedMinutes = 0;
  let pendingMinutes = 0;

  for (const row of overtimeRequests) {
    if (hrIsApprovedOvertimeStatus(row.statusCode)) {
      approvedMinutes += row.minutes ?? 0;
    } else if (hrIsPendingWorkflowStatus(row.statusCode)) {
      pendingMinutes += row.minutes ?? 0;
    }
  }

  for (const row of attendanceRows) {
    const ot = row.overtimeMinutes ?? 0;
    if (ot <= 0 || (row.id != null && linkedAttendanceIds.has(row.id))) continue;
    if (hrAttendanceRowApproved(row)) approvedMinutes += ot;
    else if (hrIsPendingWorkflowStatus(row.approvalStatus)) pendingMinutes += ot;
  }

  return { approvedMinutes, pendingMinutes };
}

export const HR_DAY_TYPE_STYLES: Record<string, string> = {
  WORKING: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  WEEKEND: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  HOLIDAY: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
  HALF_DAY: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  SPECIAL: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
};

/** Background-only tokens for schedule grid cells (keeps badge text readable). */
export const HR_DAY_TYPE_BG: Record<string, string> = {
  WORKING: 'bg-white dark:bg-slate-900',
  WEEKEND: HR_CAL_OFF_DAY_CLASS,
  HOLIDAY: 'bg-rose-50/60 dark:bg-rose-950/25',
  HALF_DAY: 'bg-amber-50/60 dark:bg-amber-950/25',
  SPECIAL: 'bg-sky-50/60 dark:bg-sky-950/25',
};

export type HrResolvedDayType = keyof typeof HR_DAY_TYPE_STYLES;

export type HrEffectiveCalendarDayInput = {
  dayType: string;
  scheduledWorkDay?: boolean;
  expectedMinutes?: number;
  shiftId?: number | null;
  shiftName?: string | null;
  shiftStartTime?: string | null;
  shiftEndTime?: string | null;
};

/** Calendar cells follow company schedule: non-working days stay off unless scheduled. */
export function hrEffectiveCalendarDayType(day: HrEffectiveCalendarDayInput): string {
  if (day.dayType === 'SPECIAL') return 'SPECIAL';
  if (day.scheduledWorkDay) {
    return day.dayType === 'HALF_DAY' ? 'HALF_DAY' : 'WORKING';
  }
  if (day.dayType === 'HALF_DAY') return 'HALF_DAY';
  if (day.dayType === 'HOLIDAY' || day.dayType === 'WEEKEND') return day.dayType;
  return 'WEEKEND';
}

export function hrFormatCalendarDayTypeLabel(dayType: string): string {
  return dayType.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

export function hrDayTypeBackground(dayType: string): string {
  return HR_DAY_TYPE_BG[dayType] ?? HR_DAY_TYPE_BG.WORKING;
}

export function hrEffectiveDayBackground(day: HrEffectiveCalendarDayInput): string {
  return hrDayTypeBackground(hrEffectiveCalendarDayType(day));
}

/** Hours label for calendar pill badges, e.g. "8 Hours" or "4h 36m". */
export function hrFormatCalendarBadgeHours(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '—';
  const hours = Math.floor(totalMinutes / 60);
  const mins = Math.round(totalMinutes % 60);
  if (mins === 0) {
    return hours === 1 ? '1 Hour' : `${hours} Hours`;
  }
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${mins}m`;
}

export type HrCalendarBadgeVariant = 'full' | 'partial' | 'leave' | 'absent' | 'active' | 'neutral' | 'overtime';

export function hrAttendanceCalendarBadge(
  status: string,
  totalMinutes: number,
  _expectedMinutes = 8 * 60,
): { variant: HrCalendarBadgeVariant; label: string } {
  if (status === 'ABSENT') {
    return { variant: 'absent', label: 'Absent' };
  }
  if (totalMinutes > 0) {
    return { variant: 'full', label: `${hrFormatMinutes(totalMinutes)} · Present` };
  }
  return { variant: 'full', label: 'Present' };
}

type InferredAbsentLeaveRow = { statusCode?: string | null };

export type HrInferredAbsentInput = {
  dayKey: string;
  scheduledWorkDay?: boolean;
  attendanceRows: AttendanceDayRow[];
  leaveRows: InferredAbsentLeaveRow[];
  /** When true (default), today and future days never show inferred absent. */
  pastDaysOnly?: boolean;
};

/**
 * Matches month-end absence logic: scheduled work day, no check-in, no approved leave.
 */
export function hrIsInferredAbsentDay({
  dayKey,
  scheduledWorkDay,
  attendanceRows,
  leaveRows,
  pastDaysOnly = true,
}: HrInferredAbsentInput): boolean {
  if (scheduledWorkDay !== true) return false;
  if (pastDaysOnly && dayKey >= todayInDisplayTimezone()) return false;
  if (attendanceRows.length > 0) return false;
  const hasApprovedLeave = leaveRows.some(
    (row) => (row.statusCode ?? '').toUpperCase() === 'APPROVED',
  );
  return !hasApprovedLeave;
}

export function hrInferredAbsentFromResolvedDay(
  dayKey: string,
  resolved: { scheduledWorkDay?: boolean } | undefined,
  attendanceRows: AttendanceDayRow[],
  leaveRows: InferredAbsentLeaveRow[],
): boolean {
  return hrIsInferredAbsentDay({
    dayKey,
    scheduledWorkDay: resolved?.scheduledWorkDay,
    attendanceRows,
    leaveRows,
  });
}

export function hrInferredAbsentTooltip(): string {
  return 'Inferred absent — scheduled work day with no check-in and no approved leave';
}

export function hrIsoDayOfWeek(date: Date): number {
  const js = date.getDay();
  return js === 0 ? 7 : js;
}

export const HR_ATTENDANCE_STYLES: Record<string, string> = {
  PRESENT: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  LATE: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  EARLY: 'bg-amber-50 text-amber-700 dark:bg-amber-900 dark:text-amber-200',
  ABSENT: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
  HOLIDAY: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200',
  WEEKEND: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  HALF_DAY: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
};

export const HR_LEAVE_STYLES: Record<string, string> = {
  APPROVED: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
  PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  SUBMITTED: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  REJECTED: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
};

export const HR_DEFAULT_LEAVE_TYPE_COLOR = '#64748b';

export function hrNormalizeLeaveTypeColor(color?: string | null): string {
  if (!color?.trim()) return HR_DEFAULT_LEAVE_TYPE_COLOR;
  const trimmed = color.trim();
  const hex = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return /^#[0-9a-fA-F]{3,8}$/.test(hex) ? hex.toLowerCase() : HR_DEFAULT_LEAVE_TYPE_COLOR;
}

/** Inline styles for leave badges using the configured leave-type color. */
export function hrLeaveTypeBadgeStyle(
  color?: string | null,
  options?: { muted?: boolean },
): Record<string, string> {
  const hex = hrNormalizeLeaveTypeColor(color);
  const muted = options?.muted ?? false;
  return {
    backgroundColor: `color-mix(in srgb, ${hex} ${muted ? 12 : 20}%, transparent)`,
    borderColor: `color-mix(in srgb, ${hex} ${muted ? 28 : 42}%, transparent)`,
    color: hex,
    opacity: muted ? '0.85' : '1',
  };
}

export function hrShiftNameStyle(color?: string | null): Record<string, string> {
  const hex = hrNormalizeLeaveTypeColor(color ?? HR_DEFAULT_SHIFT_COLOR);
  return {
    color: hex,
  };
}

export function hrShiftMonth(viewDate: Date, delta: number): Date {
  return addMonths(viewDate, delta);
}

export function hrIsInViewMonth(day: Date, viewDate: Date): boolean {
  return isSameMonth(day, viewDate);
}

type AttendanceLocationFields = {
  checkInLatitude?: number | null;
  checkInLongitude?: number | null;
  geoValid?: boolean | null;
};

export function formatAttendanceLocation(row: AttendanceLocationFields): string {
  const lat = row.checkInLatitude;
  const lng = row.checkInLongitude;
  if (lat == null || lng == null) return '—';
  const coords = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  if (row.geoValid === false) return `${coords} (outside fence)`;
  return coords;
}

export async function readCurrentGeoPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
    });
  });
}

